import { LucideIcon, TrendingUp } from "lucide-react";

type Tone = "primary" | "warning" | "success" | "destructive";

interface Props {
  label: string;
  value: string | number;
  icon: LucideIcon;
  tone: Tone;
  trendValue?: string; // e.g. "12,4%"
  trendPositive?: boolean;
  trendLabel?: string; // e.g. "vs. mês anterior"
  attention?: string; // alternative to trend pill (e.g. "requer atenção")
}

const toneMap: Record<Tone, { iconBg: string; iconColor: string; barAccent: string }> = {
  primary:    { iconBg: "bg-[hsl(225_75%_53%/0.12)]", iconColor: "text-[hsl(225_75%_53%)]", barAccent: "from-[hsl(225_75%_53%)]/70" },
  warning:    { iconBg: "bg-[hsl(28_92%_44%/0.14)]",  iconColor: "text-[hsl(28_92%_44%)]",  barAccent: "from-[hsl(28_92%_44%)]/70" },
  success:    { iconBg: "bg-[hsl(159_83%_34%/0.13)]", iconColor: "text-[hsl(159_83%_34%)]", barAccent: "from-[hsl(159_83%_34%)]/70" },
  destructive:{ iconBg: "bg-[hsl(4_70%_59%/0.13)]",   iconColor: "text-[hsl(4_70%_59%)]",   barAccent: "from-[hsl(4_70%_59%)]/70" },
};

export function DashboardKpiCard({ label, value, icon: Icon, tone, trendValue, trendPositive, trendLabel, attention }: Props) {
  const t = toneMap[tone];
  return (
    <div className="relative overflow-hidden surface-card surface-hover p-5 min-w-0">
      <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${t.barAccent} via-transparent to-transparent`} />
      <div className="flex items-start justify-between gap-3">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-muted-foreground leading-snug">
          {label}
        </p>
        <div className={`shrink-0 grid place-items-center rounded-[10px] ${t.iconBg} ${t.iconColor}`} style={{ width: 34, height: 34 }}>
          <Icon className="h-[17px] w-[17px]" strokeWidth={2.25} />
        </div>
      </div>
      <p className="mt-4 metric-number text-[28px] leading-none text-foreground">
        {value}
      </p>
      <div className="mt-4 flex items-center gap-2 min-h-[24px]">
        {trendValue && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              trendPositive
                ? "bg-[hsl(159_83%_34%/0.13)] text-[hsl(159_83%_28%)]"
                : "bg-[hsl(4_70%_59%/0.13)] text-[hsl(4_70%_45%)]"
            }`}
          >
            <TrendingUp className={`h-3 w-3 ${trendPositive ? "" : "rotate-180"}`} strokeWidth={2.5} />
            {trendValue}
          </span>
        )}
        {attention && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[hsl(4_70%_59%/0.13)] text-[hsl(4_70%_45%)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(4_70%_55%)]" />
            {attention}
          </span>
        )}
        {trendLabel && (
          <span className="text-[11px] text-muted-foreground truncate">{trendLabel}</span>
        )}
      </div>
    </div>
  );
}